import { Component } from 'react'

// ErrorBoundary
// =================================================================
// Catches render-time and lifecycle errors in its child tree and prevents
// them from propagating up — most importantly, keeping a form/modal crash
// from killing AuthContext or otherwise bouncing the user out of the app.
//
// Usage:
//   <ErrorBoundary fallback={<p>Something broke.</p>}>
//     <SomeForm />
//   </ErrorBoundary>
//
// Catches: render errors, lifecycle errors, errors in setState callbacks.
// Does NOT catch: errors in async event handlers (use try/catch there) or
// errors in the boundary's own render.
// =================================================================

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error) {
    return { error }
  }

  componentDidCatch(error, info) {
    console.error('[ErrorBoundary]', error, info)
    if (typeof this.props.onError === 'function') {
      try { this.props.onError(error, info) } catch {}
    }
  }

  reset = () => this.setState({ error: null })

  render() {
    if (this.state.error) {
      const { fallback } = this.props
      if (typeof fallback === 'function') {
        return fallback({ error: this.state.error, reset: this.reset })
      }
      if (fallback) return fallback
      return (
        <div style={{
          padding: 24, textAlign: 'center',
          fontFamily: "'JetBrains Mono', monospace", color: '#ff6a3d',
          background: 'rgba(255,106,61,.06)', border: '1px solid rgba(255,106,61,.3)',
        }}>
          <p style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>
            ◇ Something broke in this view.
          </p>
          <p style={{ fontSize: 12, color: '#9aa6b0', marginBottom: 12 }}>
            The rest of the app is still alive. {this.state.error?.message || ''}
          </p>
          <button onClick={this.reset} style={{
            padding: '8px 16px', background: '#ff6a3d', color: '#0e1116',
            border: 'none', cursor: 'pointer',
            fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
            letterSpacing: '0.08em', textTransform: 'uppercase',
          }}>RETRY</button>
        </div>
      )
    }
    return this.props.children
  }
}
